import { useCallback } from 'react';
import { graphql } from 'react-relay';

import {
	useMutationInterop,
	type MutationFunctionInteropSupportedOptions,
} from '@confluence/apollo-relay-interop/entry-points/useMutationInterop.ts';
import { expValEquals } from '@confluence/feature-experiments';

import {
	buildMutationContextForNoNetworkError,
	emptyReactionsSummary,
	OPTIMISTIC_SAVE_COMMENT_ID,
} from './commentDataManagerUtils.ts';
import type {
	useCreateInlineCommentMutation$data,
	useCreateInlineCommentMutation$variables,
} from './__generated__/useCreateInlineCommentMutation.graphql.ts';
import { useAllCommentsData, type Comment } from './useAllCommentsData.ts';

// This hook returns a mutation function to create a new inline comment. Because the new Comments
// Data Manager caches all the data for comments in Relay, we need to define the mutation here so
// that the cache can be updated fully when a new comment is created - and that means the mutation
// needs to return the full comment data structure that we are using in the returned query part of
// the mutation.  Since that returned query is quite large, we define it here in its own hook to
// avoid having it in many places in the codebase - which makes the code harder to read and could
// lead to the multiple copies getting out of sync.

// Data Mutation Definition:

// Unfortunately our Relay-friendly type generation tools don't support using GQL fragments yet -
// but in order to keep the definition of the comment fields in sync (so the cache update fragment
// is always in sync with the full query) we will need to manually make any changes to one
// definition in any others as well, until our tooling supports fragments and then we move to a
// more fragmented-based approach.  Note there are also comment queries or fragments in the
// following files that need to be kept in sync as well:
// - next/packages/comments-data-manager/src/useAllCommentsData.ts
// - next/packages/comments-data/src/hooks/useCommentById.ts
// - next/packages/comments-data-manager/src/updateCommentInCache.ts
// - next/packages/comments-data-manager/src/useCreateObjectCommentMutation.ts
// - next/packages/comments-data-manager/src/useCreateInlineReplyMutation.ts
// - next/packages/object-comments/src/components/ObjectCommentContainer.tsx

// (Note that there is another exported GraphQL mutation variable named "CreateInlineCommentMutation"
// that is exported from @confluence/inline-comments-queries. It's okay that these are the same
// variable name because the other one does not seem to be imported anywhere in the codebase at
// present, this variable is only used internally in this file, and the actual muation's name -
// useCreateInlineCommentMutation - is unique so GQL won't complain.)
const CreateInlineCommentMutation = graphql`
	mutation useCreateInlineCommentMutation(
		$pageId: ID!
		$input: CreateInlineCommentInput!
		$includeReactionsSummary: Boolean!
	) {
		createInlineComment(input: $input) {
			id
			author {
				__typename
				displayName
				permissionType
				... on KnownUser {
					accountId
					permissionType
				}
				profilePicture {
					path
				}
				type
			}
			body(representation: ATLAS_DOC_FORMAT) {
				value
			}
			container {
				id
			}
			contentStatus
			createdAt {
				value
			}
			createdAtNonLocalized
			links {
				editui
				webui
			}
			location {
				__typename
				type
				... on FooterComment {
					commentResolveProperties {
						resolved
						resolvedFriendlyDate
						resolvedTime
						resolvedUser
					}
				}
				... on InlineComment {
					inlineMarkerRef
					inlineResolveProperties {
						isDangling
						resolved
						resolvedFriendlyDate
						resolvedTime
						resolvedUser
					}
					inlineText
				}
			}
			parentId
			permissions {
				isEditable
				isRemovable
				isResolvable
			}
			reactionsSummary(pageId: $pageId, contentType: "page", childType: "comment")
				@include(if: $includeReactionsSummary) {
				ari
				containerAri
				reactionsCount
				reactionsSummaryForEmoji {
					id
					count
					emojiId
					reacted
				}
			}
			# don't need the 'replies' field because new comments are always created without any replies
			version {
				by {
					__typename
					type
					displayName
					... on KnownUser {
						accountId
					}
				}
				friendlyWhen
				number
				when
			}
		}
	}
`;

// See the comment in useCreateInlineReplyMutation.ts for why we use `any` here instead of the
// generated type. The same reasoning applies: `__typename` fields required by Apollo's writeQuery
// are not included in the generated mutation type.
export const getOptimisticResponse = ({
	userId,
	displayName,
	adf,
	pageId,
	annotationId,
	inlineText,
}: {
	userId: string;
	displayName: string;
	adf: object;
	pageId: string;
	annotationId: string;
	inlineText: string;
}): any => {
	const nowISOString = new Date().toISOString();

	return {
		createInlineComment: {
			author: {
				__typename: 'KnownUser',
				accountId: userId,
				displayName,
				permissionType: null,
				profilePicture: {
					path: userId ? `/wiki/aa-avatar/${userId}` : '',
					__typename: 'Icon',
				},
				type: 'known',
			},
			body: {
				value: JSON.stringify(adf),
				__typename: 'DocumentBody',
			},
			container: {
				id: pageId,
				__typename: 'Content',
			},
			contentStatus: 'current',
			createdAt: {
				value: '',
				__typename: 'ConfluenceDate',
			},
			createdAtNonLocalized: nowISOString, // required to correctly sort comments
			id: OPTIMISTIC_SAVE_COMMENT_ID,
			links: {
				editui: null,
				webui: null,
				__typename: 'Map_LinkType_String',
			},
			location: {
				__typename: 'InlineComment',
				type: 'INLINE',
				inlineMarkerRef: annotationId,
				inlineResolveProperties: {
					isDangling: false,
					resolved: false,
					resolvedFriendlyDate: null,
					resolvedTime: null,
					resolvedUser: null,
					__typename: 'InlineCommentResolveProperties',
				},
				inlineText,
			},
			parentId: null,
			permissions: {
				isEditable: true,
				isRemovable: true,
				isResolvable: true,
				__typename: 'CommentPermissions',
			},
			version: {
				by: {
					__typename: 'KnownUser',
					accountId: userId,
					displayName,
					type: 'user',
				},
				friendlyWhen: '',
				when: nowISOString, // required to show the creation time and tooltip correctly
				number: 1,
				__typename: 'Version',
			},
			__typename: 'Comment',
		},
	};
};

export type CreateInlineCommentInteropOptions = MutationFunctionInteropSupportedOptions<
	useCreateInlineCommentMutation$data,
	Omit<useCreateInlineCommentMutation$variables, 'includeReactionsSummary'>
> & {
	variables: Omit<useCreateInlineCommentMutation$variables, 'includeReactionsSummary'>;
};

export const useCreateInlineCommentMutation = ({
	contentId,
}: {
	contentId: string;
}): {
	createInlineCommentInterop: ({
		options,
		onCacheUpdateError,
		onCacheMiss,
	}: {
		options: CreateInlineCommentInteropOptions;
		onCacheUpdateError: (error: Error) => void;
		onCacheMiss?: (error: Error) => void;
	}) => Promise<{
		data?: useCreateInlineCommentMutation$data | undefined;
	}>;
} => {
	const { addCommentInCache } = useAllCommentsData({
		contentId,
		options: {
			skip: true, // Only using write operations, no need to fetch data
		},
	});
	const [createInlineCommentFn] = useMutationInterop<
		useCreateInlineCommentMutation$data,
		useCreateInlineCommentMutation$variables
	>(CreateInlineCommentMutation, buildMutationContextForNoNetworkError());

	const addEmptyInlineRepliesAndReactions = useCallback(
		(commentData: useCreateInlineCommentMutation$data['createInlineComment']): Comment => {
			return commentData
				? {
						...commentData,
						contributors: [],
						replies: [],
						reactionsSummary: expValEquals(
							'confluence_fe_use_create_comment_exclude_reactions',
							'isEnabled',
							true,
						)
							? emptyReactionsSummary
							: (commentData.reactionsSummary ?? emptyReactionsSummary),
					}
				: // @ts-expect-error - FIXME: This error was surfaced after enabling the `typescriptExcludeUndefinedFromNullableUnion`
					// Relay compiler option. Previously, `undefined` was incorrectly included in nullable unions in generated types; this option removes it. The owning team should fix this properly.
					undefined;
		},
		[],
	);

	// This is the interop mutation function that will be used by components that need to create
	// inline comments via the Comments Data Manager.
	const createInlineCommentInterop = useCallback(
		({
			options,
			onCacheUpdateError,
			onCacheMiss,
		}: {
			options: CreateInlineCommentInteropOptions;
			onCacheUpdateError: (error: Error) => void;
			onCacheMiss?: (error: Error) => void;
		}): Promise<{
			data?: useCreateInlineCommentMutation$data | undefined;
		}> => {
			const updateNewCommentInCache: NonNullable<
				MutationFunctionInteropSupportedOptions<
					useCreateInlineCommentMutation$data,
					useCreateInlineCommentMutation$variables
				>['update']
			> = (_dataProxy, mutationResult) => {
				addCommentInCache({
					newComment: addEmptyInlineRepliesAndReactions(mutationResult.data!.createInlineComment),
					onError: onCacheUpdateError,
					onCacheMiss,
				});
			};

			return createInlineCommentFn({
				variables: {
					...options.variables,
					includeReactionsSummary: !expValEquals(
						'confluence_fe_use_create_comment_exclude_reactions',
						'isEnabled',
						true,
					),
				},
				update: updateNewCommentInCache,
				optimisticResponse: options.optimisticResponse,
			});
		},
		[addCommentInCache, addEmptyInlineRepliesAndReactions, createInlineCommentFn],
	);

	return { createInlineCommentInterop };
};
